/**
 * One-way sync from the dashboard to Google Calendar.
 *
 * The dashboard is the source of truth. Google has no read-only flag for
 * events on a calendar you own, so an event dragged in Calendar cannot be
 * prevented; instead every load reconciles and puts it back.
 *
 * The decision of what the calendar *should* contain is a pure function
 * (`desiredEvent`, `planReconcile`), so it is testable without touching the
 * network. Everything stateful lives in `apply`.
 */
import { calendar } from "../lib/calendar";
import { desiredEvent, planReconcile, type Action } from "./calendarPlan";
import type { Project, Task } from "../types";

/** Event ids that changed, as task id to new value ("" means cleared). */
export type IdChanges = Map<string, string>;

export interface ApplyResult {
  changes: IdChanges;
  created: number;
  updated: number;
  deleted: number;
  failures: number;
}

async function runAction(
  action: Action,
  calendarId: string,
  token: string,
  result: ApplyResult
): Promise<void> {
  switch (action.kind) {
    case "create": {
      const id = await calendar.create(
        calendarId,
        action.desired.summary,
        action.desired.date,
        token
      );
      result.changes.set(action.task.id, id);
      result.created++;
      return;
    }
    case "update":
      await calendar.update(
        calendarId,
        action.eventId,
        action.desired.summary,
        action.desired.date,
        token
      );
      result.updated++;
      return;
    case "delete":
      await calendar.remove(calendarId, action.eventId, token);
      result.changes.set(action.task.id, "");
      result.deleted++;
      return;
  }
}

/**
 * Carry out a plan.
 *
 * One failing event must not abandon the rest, and must never be allowed to
 * propagate into the caller's sheet write. Failures are counted and the
 * successful id changes are still returned.
 */
export async function apply(
  actions: Action[],
  calendarId: string,
  token: string
): Promise<ApplyResult> {
  const result: ApplyResult = {
    changes: new Map(),
    created: 0,
    updated: 0,
    deleted: 0,
    failures: 0,
  };
  for (const action of actions) {
    try {
      await runAction(action, calendarId, token, result);
    } catch {
      result.failures++;
    }
  }
  return result;
}

/** The full load-time reconcile: one list call, then only what differs. */
export async function reconcile(
  tasks: Task[],
  projects: Map<string, Project>,
  token: string
): Promise<ApplyResult & { calendarId: string }> {
  const calendarId = await calendar.ensure(token);
  const events = await calendar.list(calendarId, token);
  const name = (task: Task) => projects.get(task.projectId)?.name ?? task.projectName;
  const plan = planReconcile(tasks, name, events);
  const result = await apply(plan, calendarId, token);
  return { ...result, calendarId };
}

/**
 * Sync one task after it has already been written to the sheet.
 *
 * Returns the task's new calendar event id, or undefined when nothing needed
 * to change. Never throws: a calendar failure must not undo a saved task.
 */
export async function syncOne(
  task: Task,
  projectName: string,
  calendarId: string,
  token: string
): Promise<string | undefined> {
  const want = desiredEvent(task, projectName);
  try {
    if (want && !task.calendarEventId) {
      return await calendar.create(calendarId, want.summary, want.date, token);
    }
    if (want && task.calendarEventId) {
      await calendar.update(
        calendarId,
        task.calendarEventId,
        want.summary,
        want.date,
        token
      );
      return undefined;
    }
    if (!want && task.calendarEventId) {
      await calendar.remove(calendarId, task.calendarEventId, token);
      return "";
    }
  } catch {
    // Swallowed deliberately. The next load's reconcile repairs it.
    return undefined;
  }
  return undefined;
}
